import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { btnPrimary, btnSecondary, inputCls, textareaCls } from "../../components/admin/FormComponents"
import {
  KEY_ISSUE_LABEL, TYPE_LABEL, downloadJson, fmtP, formatValue, isFreeType, verdictLabel,
  type Feedback, type JevSlot, type KeyIssue, type Summary, type TestResult,
} from "./section-quiz-test/model"

// Resultado de una prueba — openspec/changes/section-quiz-expert-test. Compara el calificador
// actual con Jev, muestra el detalle de Jev y recoge el feedback del experto (autoguardado).

type RunData = {
  id: string
  section_quiz_id: string
  status: "in_progress" | "graded"
  created_at: string
  results: TestResult[]
  feedback: Feedback[]
  summary: Summary
}

type SaveState = "idle" | "saving" | "saved" | "error"

function Badge({ tone, children }: { tone: "green" | "red" | "amber" | "gray" | "blue"; children: React.ReactNode }) {
  const cls = {
    green: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
    red: "bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    gray: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  }[tone]
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{children}</span>
}

function currentBadge(r: TestResult) {
  if (r.current.status === "unavailable") return <Badge tone="gray">Actual: no calificó</Badge>
  if (r.current.status === "skipped") return <Badge tone="gray">Actual: sin responder</Badge>
  return <Badge tone={r.current.verdict ? "green" : "red"}>Actual: {verdictLabel(r.current.verdict)}</Badge>
}

function jevBadge(j: JevSlot) {
  switch (j.status) {
    case "ok":
      return <Badge tone={j.verdict ? "green" : "red"}>Jev: {verdictLabel(j.verdict)}{j.vetoed ? " (vetada)" : ""}</Badge>
    case "unavailable": return <Badge tone="gray">Jev: no disponible</Badge>
    case "not_applicable": return <Badge tone="gray">Jev: no aplica (cerrada)</Badge>
    default: return <Badge tone="gray">Jev: sin responder</Badge>
  }
}

function Bar({ p }: { p: number }) {
  return (
    <span className="inline-block h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 align-middle dark:bg-gray-700" aria-hidden>
      <span className="block h-full bg-brand-500" style={{ width: `${Math.round(p * 100)}%` }} />
    </span>
  )
}

function JevDetail({ r }: { r: TestResult }) {
  const j = r.jev
  const k = r.key_check
  if (j.status !== "ok" && k.status !== "ok") return null
  return (
    <details className="mt-3 text-sm">
      <summary className="cursor-pointer select-none font-medium text-gray-700 dark:text-gray-200">Detalle de Jev</summary>
      <div className="mt-2 space-y-3 pl-1">
        {j.status === "ok" && (
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Criterios evaluados (P de que se cumpla)</p>
            <ul className="space-y-1">
              {j.criteria.map((c, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <Bar p={c.p} />
                  <span className="w-10 tabular-nums">{fmtP(c.p)}</span>
                  <span className="text-gray-700 dark:text-gray-300">{c.text}{c.matchedLocally ? " · coincidió con la clave sin consultar a Jev" : ""}</span>
                </li>
              ))}
            </ul>
            {j.vetos && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Vetos (invalidan si ≥ 0.70): contradice la clave {fmtP(j.vetos.contradiction)} · afirmación falsa {fmtP(j.vetos.falseClaim)} · lista de términos {fmtP(j.vetos.keywordList)}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-400">Modelo {j.modelVersion}</p>
          </div>
        )}
        {k.status === "ok" && (
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              Verificación de la clave {k.usedLessonText ? "contra el texto de la lección" : "(sin texto de lección)"}
            </p>
            <ul className="space-y-1">
              {k.checks.map((c, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <Bar p={c.p} />
                  <span className="w-10 tabular-nums">{fmtP(c.p)}</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {c.instruction}{" "}
                    <span className="text-xs text-gray-400">({c.gate ? (c.expect === "high" ? "debe ser alta" : "debe ser baja") : "informativa"})</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  )
}

function FeedbackForm({
  result, feedback, state, onSave,
}: {
  result: TestResult
  feedback: Feedback | undefined
  state: SaveState
  onSave: (patch: Partial<Omit<Feedback, "question_index">>) => void
}) {
  const [comment, setComment] = useState(feedback?.comment ?? "")
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const onComment = (value: string) => {
    setComment(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => onSave({ comment: value.trim() === "" ? null : value }), 700)
  }

  const name = `should-${result.index}`
  const free = isFreeType(result.question_type)
  return (
    <fieldset className="mt-4 space-y-3 border-t border-dashed border-gray-200 pt-3 dark:border-gray-700">
      <legend className="sr-only">Tu feedback sobre la pregunta {result.index + 1}</legend>

      {free && (
        <div role="radiogroup" aria-label="Cómo debió calificarse esta respuesta" className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-200">Esta respuesta debió calificarse:</span>
          {(["correct", "incorrect"] as const).map((v) => (
            <label key={v} className="flex cursor-pointer items-center gap-1.5 text-gray-800 dark:text-gray-200">
              <input type="radio" name={name} checked={feedback?.answer_should_be === v} onChange={() => onSave({ answer_should_be: v })} />
              {v === "correct" ? "Correcta" : "Incorrecta"}
            </label>
          ))}
        </div>
      )}

      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
        <span className="font-medium">La clave y la pregunta:</span>
        <select
          className={`${inputCls} max-w-xs`}
          value={feedback?.key_issue ?? ""}
          onChange={(e) => onSave({ key_issue: e.target.value === "" ? null : (e.target.value as KeyIssue) })}
        >
          <option value="">Sin evaluar</option>
          {(Object.keys(KEY_ISSUE_LABEL) as KeyIssue[]).map((k) => <option key={k} value={k}>{KEY_ISSUE_LABEL[k]}</option>)}
        </select>
      </label>

      <label className="block text-sm text-gray-700 dark:text-gray-200">
        <span className="font-medium">Comentario (opcional)</span>
        <textarea rows={2} className={`${textareaCls} mt-1`} value={comment} onChange={(e) => onComment(e.target.value)} />
      </label>

      <p className="text-xs text-gray-400" role="status" aria-live="polite">
        {state === "saving" ? "Guardando…" : state === "saved" ? "Guardado" : state === "error" ? "No se pudo guardar; vuelve a intentarlo" : ""}
      </p>
    </fieldset>
  )
}

export default function SectionQuizTestResultPage() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const [run, setRun] = useState<RunData | null>(null)
  const [loading, setLoading] = useState(true)
  const [feedback, setFeedback] = useState<Record<number, Feedback>>({})
  const [summary, setSummary] = useState<Summary | null>(null)
  const [saveState, setSaveState] = useState<Record<number, SaveState>>({})
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async () => {
    if (!runId) return
    setLoading(true)
    try {
      const res = await api.get(`/academy/section-quizzes/test/runs/${runId}`)
      const data = res.data.data as RunData
      setRun(data)
      setFeedback(Object.fromEntries((data.feedback ?? []).map((f) => [f.question_index, f])))
      setSummary(data.summary ?? null)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [runId])

  useEffect(() => { void load() }, [load])

  const save = async (index: number, patch: Partial<Omit<Feedback, "question_index">>) => {
    if (!runId) return
    setSaveState((s) => ({ ...s, [index]: "saving" }))
    try {
      const res = await api.put(`/academy/section-quizzes/test/runs/${runId}/feedback`, { question_index: index, ...patch })
      const saved = res.data.data.feedback as Feedback
      setFeedback((f) => ({ ...f, [index]: saved }))
      setSummary(res.data.data.summary)
      setSaveState((s) => ({ ...s, [index]: "saved" }))
    } catch (err) {
      setSaveState((s) => ({ ...s, [index]: "error" }))
      toastError(err)
    }
  }

  // Desacuerdos primero: son lo que el experto necesita juzgar.
  const ordered = useMemo(
    () => [...(run?.results ?? [])].sort((a, b) => Number(b.disagreement) - Number(a.disagreement) || a.index - b.index),
    [run]
  )

  const exportFeedback = async () => {
    if (!runId) return
    try {
      const res = await api.get(`/academy/section-quizzes/test/export`, { params: { run_ids: runId } })
      const { grading, validation } = res.data.data as { grading: unknown[]; validation: unknown[] }
      const short = runId.slice(0, 8)
      if (grading.length) downloadJson(`feedback-${short}-calificacion.json`, grading)
      if (validation.length) downloadJson(`feedback-${short}-validacion-claves.json`, validation)
      if (!grading.length && !validation.length) toastError(new Error("Aún no hay feedback que exportar: evalúa al menos una respuesta o una clave."))
      else toastSuccess("Exportado en el formato del benchmark")
    } catch (err) {
      toastError(err)
    }
  }

  const remove = async () => {
    if (!runId || !run) return
    try {
      await api.delete(`/academy/section-quizzes/test/runs/${runId}`)
      toastSuccess("Prueba eliminada")
      navigate(`/instructor/section-quizzes/${run.section_quiz_id}/tests`)
    } catch (err) {
      toastError(err)
    }
  }

  const s = summary
  const pctOf = (agree: number) => (s && s.labeled > 0 ? `${agree} de ${s.labeled}` : "—")

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, "Resultado de la prueba"]}
      title="Resultado de la prueba"
      subtitle="Compara cómo calificó cada calificador, revisa el detalle de Jev y dinos si la calificación y la clave eran correctas."
      actions={
        <>
          <button type="button" className={btnSecondary} onClick={() => void exportFeedback()}>Exportar feedback</button>
          {run && <button type="button" className={btnSecondary} onClick={() => navigate(`/instructor/section-quizzes/${run.section_quiz_id}/tests`)}>Historial</button>}
          {run && <button type="button" className={btnPrimary} onClick={() => navigate(`/instructor/section-quizzes/${run.section_quiz_id}/test`)}>Probar de nuevo</button>}
        </>
      }
      tabs={[]}
      metrics={s ? [
        { label: "Preguntas", value: String(s.questions) },
        { label: "Desacuerdos", value: String(s.disagreements), tone: s.disagreements > 0 ? "warning" : "good" },
        { label: "Evaluadas por ti", value: String(s.labeled) },
        { label: "Calificador actual acertó", value: pctOf(s.current.agree) },
        { label: "Jev acertó", value: s.jev.unavailable > 0 && s.jev.agree + s.jev.disagree === 0 ? "no disponible" : pctOf(s.jev.agree) },
        { label: "Claves con problema", value: String(s.keyIssues), tone: s.keyIssues > 0 ? "danger" : "good" },
      ] : []}
    >
      {loading && <p className="text-sm text-gray-500" role="status">Cargando…</p>}
      {!loading && run && run.status !== "graded" && <p className="text-sm text-gray-600">Esta prueba todavía no se envió.</p>}

      {run?.status === "graded" && (
        <>
          <ol className="max-w-4xl">
            {ordered.map((r) => {
              const free = isFreeType(r.question_type)
              return (
                <li key={r.index} className="border-b border-gray-100 py-6 first:pt-0 dark:border-gray-800">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Pregunta {r.index + 1} · {TYPE_LABEL[r.question_type] ?? r.question_type}
                    </p>
                    {r.disagreement && <Badge tone="amber">Desacuerdo</Badge>}
                    {currentBadge(r)}
                    {free || r.jev.status !== "not_applicable" ? jevBadge(r.jev) : null}
                    {r.key_check.status === "ok" && (r.key_check.supported
                      ? <Badge tone="green">Clave respaldada</Badge>
                      : <Badge tone="red">Clave no respaldada por Jev</Badge>)}
                  </div>

                  <p className="mb-3 whitespace-pre-line text-base font-medium text-gray-900 dark:text-white">{r.question_text}</p>

                  <dl className="grid gap-4 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Tu respuesta</dt>
                      <dd className="mt-0.5 whitespace-pre-line text-gray-800 dark:text-gray-200">{formatValue(r.question_type, r.answer, r.content)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Clave</dt>
                      <dd className="mt-0.5 whitespace-pre-line text-gray-800 dark:text-gray-200">{formatValue(r.question_type, r.correct_answer, r.content)}</dd>
                    </div>
                  </dl>
                  {r.explanation && <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Explicación del examen: {r.explanation}</p>}

                  <JevDetail r={r} />
                  <FeedbackForm
                    result={r}
                    feedback={feedback[r.index]}
                    state={saveState[r.index] ?? "idle"}
                    onSave={(patch) => void save(r.index, patch)}
                  />
                </li>
              )
            })}
          </ol>

          <div className="mt-8 flex items-center gap-3 border-t border-gray-200 pt-4 dark:border-gray-800">
            {!confirmDelete ? (
              <button type="button" className={btnSecondary} onClick={() => setConfirmDelete(true)}>Eliminar esta prueba</button>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-300">Se borrarán la prueba y su feedback. No afecta ningún dato real.</p>
                <button type="button" className={btnPrimary} onClick={() => void remove()}>Confirmar eliminación</button>
                <button type="button" className={btnSecondary} onClick={() => setConfirmDelete(false)}>Cancelar</button>
              </>
            )}
          </div>
        </>
      )}
    </ModuleLayout>
  )
}
